import { useTranslation } from "react-i18next";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { Skeleton } from "./Skeleton";
import {
  CANON_LINE_PRIMARY,
  canonAxisLabel,
  canonAxisTick,
  canonGridProps,
  canonLegendProps,
  canonTooltipContentStyle,
  canonTooltipItemStyle,
  canonTooltipLabelStyle,
  legendLabels,
} from "./canon/CanonChart";

/**
 * CareerPeaksCard — multi-year personal bests (CTL / VO2max / CP / CSS) across
 * seasons. Presentational: data comes in as props so it renders standalone in
 * the design system with sample values. The athlete's Profile screen wraps it
 * with `useCareerPeaks()` (GET /athlete/analytics/career-peaks).
 *
 * Complements the single-year СЕЗОН ИТОГО card — an elite athlete wants career
 * peaks, not just the current 12-week window.
 */
export interface CareerYearPeak {
  year: number;
  peak_ctl: number | null;
  /** Откуда пришёл пик: "storica" — наш движок, "intervals" — импорт. */
  ctl_source?: string | null;
  ctl_days?: number | null;
  /** Наблюдали меньше половины года: «пик» может быть пиком фрагмента. */
  ctl_partial?: boolean | null;
  best_vo2max: number | null;
  best_power_watts: number | null;
  best_css_sec_per_100m: number | null;
  best_vdot: number | null;
  total_hours: number;
  races: number;
}

export interface CareerPeaksCardProps {
  years: CareerYearPeak[];
  alltimePeakCtl?: number | null;
  alltimePeakCtlDate?: string | null;
  alltimeBestVo2max?: number | null;
  alltimeBestPowerWatts?: number | null;
  alltimeBestCssSecPer100m?: number | null;
  loading?: boolean;
  className?: string;
}

function paceCss(s: number | null | undefined): string {
  return s == null ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
}

/**
 * Есть ли пик формы хоть у одного сезона.
 *
 * Вынесено функцией, потому что от этого решения зависят ТРИ вещи разом:
 * вторая ось, линия и запись в легенде. Recharts заводит запись легенды для
 * `<Line>` независимо от того, есть ли у ряда точки, — то есть при пустом
 * CTL легенда обещала бы ряд, которого на графике нет. У атлета без кэша
 * формы это все годы сразу (найдено на живом экране).
 */
export function hasPeakCtl(years: readonly CareerYearPeak[]): boolean {
  return years.some((y) => y.peak_ctl != null);
}

export function CareerPeaksCard({
  years,
  alltimePeakCtl = null,
  alltimePeakCtlDate = null,
  alltimeBestVo2max = null,
  alltimeBestPowerWatts = null,
  alltimeBestCssSecPer100m = null,
  loading = false,
  className = "",
}: CareerPeaksCardProps) {
  const { t } = useTranslation("profile");

  if (!loading && years.length === 0) return null; // hide until history exists

  /** Есть ли пик формы хоть у одного года — от этого зависит вторая шкала. */
  const hasCtl = hasPeakCtl(years);
  /** Есть ли неполные годы — от этого зависит, нужна ли расшифровка звёздочки. */
  const anyPartial = years.some((y) => y.ctl_partial === true);

  const alltime = [
    {
      key: "ctl",
      label: t("career.ctl", { defaultValue: "Пик CTL" }),
      value:
        alltimePeakCtl != null
          ? `${Math.round(alltimePeakCtl)}${
              alltimePeakCtlDate ? ` · ${new Date(alltimePeakCtlDate).getFullYear()}` : ""
            }`
          : "—",
    },
    {
      key: "vo2",
      label: t("career.vo2max", { defaultValue: "VO2max" }),
      value: alltimeBestVo2max != null ? alltimeBestVo2max.toFixed(1) : "—",
    },
    {
      key: "pwr",
      label: t("career.power", { defaultValue: "CP/FTP" }),
      value: alltimeBestPowerWatts != null ? `${alltimeBestPowerWatts} ${t("career.col_power", { defaultValue: "Вт" })}` : "—",
    },
    {
      key: "css",
      label: t("career.css", { defaultValue: "CSS /100м" }),
      value: paceCss(alltimeBestCssSecPer100m),
    },
  ];

  return (
    <div
      data-testid="career-peaks-card"
      className={`indigo-card p-4 space-y-3 ${className}`.trim()}
      aria-busy={loading}
    >
      <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
        {t("career.eyebrow", { defaultValue: "ПИКИ КАРЬЕРЫ · ЛУЧШЕЕ" })}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {alltime.map((c) => (
          <div key={c.key} data-testid={`career-alltime-${c.key}`} className="space-y-0.5">
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted">
              {c.label}
            </div>
            <div className="text-base font-semibold text-fg-primary num-display tabular-nums">
              {loading && years.length === 0 ? <Skeleton className="h-5 w-14" /> : c.value}
            </div>
          </div>
        ))}
      </div>
      {/* СЕЗОНЫ РЯДОМ, А НЕ СТОЛБИКОМ ЧИСЕЛ.
          Таблица ниже отвечает «сколько было в таком-то году» — по ней сверяют
          конкретное значение. Вопрос «росла ли карьера» она не отвечает:
          чтобы увидеть направление, читателю приходится держать пять чисел в
          голове и вычитать их глазами.

          Часы — столбиками, пик формы — линией: это разные величины, и общая
          шкала сделала бы вид, что их можно сравнивать. Две оси подписаны, а
          то «600» и «95» стоят рядом одинаково правдоподобно.

          Два года — уже сравнение; на одном рисовать нечего, и график
          прячется. */}
      {years.length >= 2 && (
        <div style={{ width: "100%", height: 150 }} data-testid="career-years-chart">
          <ResponsiveContainer>
            <ComposedChart data={years} margin={{ top: 6, right: 4, bottom: 2, left: 0 }}>
              <CartesianGrid {...canonGridProps} />
              <XAxis dataKey="year" tick={canonAxisTick} axisLine={false} tickLine={false} />
              <YAxis
                yAxisId="hours"
                width={40}
                tick={canonAxisTick}
                axisLine={false}
                tickLine={false}
                label={canonAxisLabel(t("career.col_hours_axis", { defaultValue: "часы" }))}
              />
              {/* Правая ось и линия появляются ТОЛЬКО когда пик формы есть
                  хоть у одного года. Иначе recharts всё равно заводит запись
                  в легенде, и та обещает ряд, которого на графике нет — а у
                  атлета без кэша формы это все годы сразу.

                  Ось и линия включаются ОДНИМ условием: `<Line>` со ссылкой
                  на несуществующий yAxisId не молчит, а бросает Invariant и
                  гасит весь экран. */}
              {hasCtl && (
                <YAxis
                  yAxisId="ctl"
                  orientation="right"
                  width={38}
                  tick={canonAxisTick}
                  axisLine={false}
                  tickLine={false}
                  label={canonAxisLabel(t("career.ctl_axis", { defaultValue: "пик формы" }), "right")}
                />
              )}
              <Tooltip
                contentStyle={canonTooltipContentStyle}
                labelStyle={canonTooltipLabelStyle}
                itemStyle={canonTooltipItemStyle}
              />
              <Legend
                {...canonLegendProps}
                formatter={legendLabels({
                  total_hours: t("career.col_hours_axis", { defaultValue: "часы" }),
                  peak_ctl: t("career.ctl_axis", { defaultValue: "пик формы" }),
                })}
              />
              <Bar yAxisId="hours" dataKey="total_hours" fill="var(--accent)" fillOpacity={0.35} radius={[2, 2, 0, 0]} isAnimationActive={false} />
              {/* connectNulls: год без пика формы (не было расчёта) не должен
                  рвать линию на два куска — это читалось бы как два периода
                  карьеры вместо одного пробела. */}
              {/* Точка неполного года — ПОЛАЯ. Убрать её нельзя: разрыв
                  читался бы как «не тренировался», а это неправда — данных
                  просто мало. Но и рисовать наравне тоже нельзя: тридцать
                  три импортированных дня выглядели бы сезонным пиком. */}
              {hasCtl && (
                <Line
                  yAxisId="ctl"
                  type="monotone"
                  dataKey="peak_ctl"
                  stroke={CANON_LINE_PRIMARY}
                  strokeWidth={2}
                  connectNulls
                  isAnimationActive={false}
                  dot={(props: { cx?: number; cy?: number; payload?: CareerYearPeak; index?: number }) => {
                    const { cx, cy, payload } = props;
                    if (cx == null || cy == null) return <g key={`d${props.index}`} />;
                    const partial = payload?.ctl_partial === true;
                    return (
                      <circle
                        key={`d${props.index}`}
                        cx={cx}
                        cy={cy}
                        r={2.5}
                        fill={partial ? "var(--bg-base)" : CANON_LINE_PRIMARY}
                        stroke={CANON_LINE_PRIMARY}
                        strokeWidth={partial ? 1.2 : 0}
                      />
                    );
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {years.length > 0 && (
        <div className="overflow-x-auto -mx-1 px-1">
          <table className="w-full text-[11px] tabular-nums">
            <thead>
              <tr className="text-fg-muted font-mono uppercase tracking-[0.1em]">
                <th className="text-left font-medium py-1">{t("career.col_year", { defaultValue: "Год" })}</th>
                <th className="text-right font-medium py-1">CTL</th>
                <th className="text-right font-medium py-1">VO2</th>
                <th className="text-right font-medium py-1">{t("career.col_power", { defaultValue: "Вт" })}</th>
                <th className="text-right font-medium py-1">{t("career.col_hours", { defaultValue: "Ч" })}</th>
              </tr>
            </thead>
            <tbody>
              {years.map((y) => (
                <tr key={y.year} data-testid={`career-year-${y.year}`} className="border-t border-border">
                  <td className="text-left py-1 font-semibold text-fg-primary">{y.year}</td>
                  {/* Звёздочка у неполного года объяснена подписью под таблицей,
                      а не цветом: цвет здесь читался бы как «плохая форма». */}
                  <td className="text-right py-1 text-fg-secondary">
                    {y.peak_ctl != null ? Math.round(y.peak_ctl) : "—"}
                    {y.ctl_partial === true && (
                      <span
                        className="text-fg-muted"
                        data-testid={`career-partial-${y.year}`}
                        title={t("career.partial_title", {
                          defaultValue: "{{days}} дн. с данными — меньше половины года",
                          days: y.ctl_days ?? 0,
                        })}
                      >*</span>
                    )}
                  </td>
                  <td className="text-right py-1 text-fg-secondary">{y.best_vo2max != null ? y.best_vo2max.toFixed(1) : "—"}</td>
                  <td className="text-right py-1 text-fg-secondary">{y.best_power_watts ?? "—"}</td>
                  <td className="text-right py-1 text-fg-secondary">{Math.round(y.total_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-fg-secondary leading-relaxed">
        {t("career.footer", {
          defaultValue: "Личные пики по сезонам. Пороги — из сделанных тестов.",
        })}
        {anyPartial && (
          <>
            {" "}
            <span data-testid="career-partial-note">
              {t("career.partial_note", {
                defaultValue:
                  "Звёздочкой помечен год, за который данных меньше половины: там это пик наблюдённого куска, а не сезона.",
              })}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
